/* eslint-disable no-unsafe-optional-chaining */
import { Autocomplete, Card, CircularProgress, Grid, Stack } from "@mui/material";
import { fetchUsers } from "apis/compete";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { enqueueSnackbar } from "notistack";
import DashboardLayout from "utils/LayoutContainers/DashboardLayout";
import DashboardNavbar from "utils/Navbars/DashboardNavbar";
import Loader from "utils/Loader";
import { competeAction } from "redux/slice/compete";
import ListingTable from "./components/ListingTable";

const prefix = process.env.REACT_APP_CDN_ASSET_PREFIX || "/assets";
const Trophy = `${prefix}/images/badges/trophy.png`;
const AlysioBadge = `${prefix}/images/badges/badge_alysio.png`;

function Competitions() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.only("xs"));
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const widgetsData = useSelector((state) => state.compete.widgets);
  const {
    data: openCompetitions,
    count: totalOpenCompetitions,
    loading: openCompetitionsLoading,
  } = useSelector((state) => state.compete.open);
  const {
    data: upcomingCompetitions,
    count: totalUpcomingCompetitions,
    loading: upcomingCompetitionsLoading,
  } = useSelector((state) => state.compete.upcoming);
  const {
    data: closedCompetitions,
    count: totalClosedCompetitions,
    loading: closedCompetitionsLoading,
  } = useSelector((state) => state.compete.closed);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("initial");
  const [usersLoading, setUsersLoading] = useState(false);
  const [openCompetitionsPayload, setOpenCompetitionsPayload] = useState({
    pageNumber: 0,
    pageSize: 10,
    status: "running",
  });
  const [upcomingCompetitionsPayload, setUpcomingCompetitionsPayload] = useState({
    pageNumber: 0,
    pageSize: 10,
    status: "open",
  });
  const [closedCompetitionsPayload, setClosedCompetitionsPayload] = useState({
    pageNumber: 0,
    pageSize: 10,
    status: "closed",
  });

  useEffect(() => {
    if (selectedUser !== "initial") {
      dispatch(competeAction.getWidgetsDataRequest({ userId: selectedUser?.id || loginUser.id }));
    }
  }, [selectedUser]);

  useEffect(() => {
    if (selectedUser !== "initial") {
      dispatch(
        competeAction.getOpenCompetitionsRequest({
          ...openCompetitionsPayload,
          userId: selectedUser?.id || loginUser.id,
        })
      );
    }
  }, [openCompetitionsPayload, selectedUser]);

  useEffect(() => {
    const fetchOpenCompetitions = async () => {
      if (selectedUser !== "initial") {
        dispatch(
          competeAction.getOpenCompetitionsRequest({
            ...openCompetitionsPayload,
            userId: selectedUser?.id || loginUser.id,
          })
        );
      }
    };

    fetchOpenCompetitions();

    const fetchInterval = setInterval(
      () => {
        fetchOpenCompetitions();
      },
      1000 * 60 * 30
    );

    return () => clearInterval(fetchInterval);
  }, [openCompetitionsPayload, selectedUser]);

  useEffect(() => {
    if (selectedUser !== "initial") {
      dispatch(
        competeAction.getUpcomingCompetitionsRequest({
          ...upcomingCompetitionsPayload,
          userId: selectedUser?.id || loginUser.id,
        })
      );
    }
  }, [upcomingCompetitionsPayload, selectedUser]);

  useEffect(() => {
    if (selectedUser !== "initial" && loginUser?.type === "user") {
      dispatch(
        competeAction.getClosedCompetitionsRequest({
          ...closedCompetitionsPayload,
          userId: selectedUser?.id || loginUser.id,
        })
      );
    }
  }, [closedCompetitionsPayload, selectedUser]);

  const getUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await fetchUsers();
      if (response?.data?.success) {
        setUsers(
          response.data.data.map((user) => ({
            id: user?.id,
            name: user?.name,
          }))
        );
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setUsersLoading(false);
    } catch (error) {
      setUsersLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    getUsers();
    setSelectedUser({
      id: loginUser?.id,
      name: loginUser?.name,
    });
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox className="competitions-wrapper">
        <MDBox sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Autocomplete
            sx={{ width: "50%", maxWidth: "150px" }}
            id="search"
            options={users}
            value={users?.find((user) => user?.id === selectedUser?.id) || null}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onChange={(_, value) => {
              setSelectedUser(value);
              setOpenCompetitionsPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
              setUpcomingCompetitionsPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
              setClosedCompetitionsPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
            }}
            renderInput={(params) => (
              <MDInput
                {...params}
                label="Search User"
                placeholder="Search User"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </MDBox>
        <MDBox sx={{ position: "relative", paddingTop: "1px", paddingBottom: "16px", mt: 2 }}>
          <Loader loading={widgetsData.loading} />
          <Grid container spacing={3} className="widget-grid">
            {isMobileDisplay ? (
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 1, height: "100%" }}>
                  <MDBox className="widget-box">
                    <MDBox className="image-wrapper">
                      <img src={Trophy} alt="trophy" />
                    </MDBox>
                    <Grid container direction="column">
                      <Grid item className="grid-item">
                        <MDTypography variant="subtitle1" className="title">
                          {selectedUser && selectedUser?.id !== loginUser.id
                            ? `${selectedUser?.name}'s`
                            : "My"}{" "}
                          Total Wins
                        </MDTypography>
                        <MDTypography variant="h4" className="number-text">
                          {widgetsData?.wins}
                        </MDTypography>
                      </Grid>
                      <Grid item className="grid-item">
                        <MDTypography variant="subtitle1" className="title">
                          {selectedUser && selectedUser?.id !== loginUser.id
                            ? `${selectedUser?.name}'s`
                            : "My"}{" "}
                          Average Rank
                        </MDTypography>
                        <MDTypography variant="h4" className="number-text">
                          {widgetsData?.averageRank % 1 === 0
                            ? widgetsData?.averageRank
                            : widgetsData?.averageRank.toFixed(1)}
                        </MDTypography>
                      </Grid>
                      <Grid item className="grid-item">
                        <MDTypography variant="subtitle1" className="title">
                          Open
                        </MDTypography>
                        <MDTypography variant="h4" className="number-text">
                          {widgetsData?.open}
                        </MDTypography>
                      </Grid>
                      <Grid item className="grid-item">
                        <MDTypography className="title" variant="subtitle1">
                          Upcoming
                        </MDTypography>
                        <MDTypography className="number-text" variant="h4">
                          {widgetsData?.upcoming}
                        </MDTypography>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
            ) : (
              <>
                {loginUser?.type === "user" && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ p: 1, height: "100%" }}>
                      <MDBox className="widget-box">
                        <MDBox className="image-wrapper">
                          <img src={Trophy} alt="trophy" />
                        </MDBox>
                        <Stack spacing={0.5} className="widget-text">
                          <MDTypography variant="h4" className="number-text">
                            {widgetsData?.wins}
                          </MDTypography>
                          <MDTypography variant="subtitle1" className="title">
                            {selectedUser && selectedUser?.id !== loginUser.id
                              ? `${selectedUser?.name}'s`
                              : "My"}{" "}
                            Total Wins
                          </MDTypography>
                        </Stack>
                      </MDBox>
                    </Card>
                  </Grid>
                )}
                {loginUser?.type === "user" && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ p: 1, height: "100%" }}>
                      <MDBox className="widget-box">
                        <MDBox className="image-wrapper">
                          <img src={AlysioBadge} alt="trophy" />
                        </MDBox>
                        <Stack spacing={0.5} className="widget-text">
                          <MDTypography variant="h4" className="number-text">
                            {widgetsData?.averageRank % 1 === 0
                              ? widgetsData?.averageRank
                              : widgetsData?.averageRank.toFixed(2)}
                          </MDTypography>
                          <MDTypography variant="subtitle1" className="title">
                            {selectedUser && selectedUser?.id !== loginUser.id
                              ? `${selectedUser?.name}'s`
                              : "My"}{" "}
                            Average Rank
                          </MDTypography>
                        </Stack>
                      </MDBox>
                    </Card>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 1, height: "100%" }}>
                    <MDBox className="widget-box">
                      <Stack spacing={0.5} className="widget-text">
                        <MDTypography variant="h4" className="number-text">
                          {widgetsData?.open}
                        </MDTypography>
                        <MDTypography variant="subtitle1" className="title">
                          Open
                        </MDTypography>
                      </Stack>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ p: 1, height: "100%" }}>
                    <MDBox className="widget-box">
                      <Stack spacing={0.5} className="widget-text">
                        <MDTypography className="number-text" variant="h4">
                          {widgetsData?.upcoming}
                        </MDTypography>
                        <MDTypography className="title" variant="subtitle1">
                          Upcoming
                        </MDTypography>
                      </Stack>
                    </MDBox>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>
        </MDBox>
        <Stack spacing={3}>
          <ListingTable
            competitions={openCompetitions}
            count={totalOpenCompetitions}
            payload={openCompetitionsPayload}
            setPayload={setOpenCompetitionsPayload}
            status="Open"
            selectedUser={selectedUser}
            loading={openCompetitionsLoading}
          />
          <ListingTable
            competitions={upcomingCompetitions}
            count={totalUpcomingCompetitions}
            payload={upcomingCompetitions}
            setPayload={setUpcomingCompetitionsPayload}
            status="Upcoming"
            selectedUser={selectedUser}
            loading={upcomingCompetitionsLoading}
          />
          {loginUser?.type === "user" && (
            <ListingTable
              competitions={closedCompetitions}
              count={totalClosedCompetitions}
              payload={closedCompetitionsPayload}
              setPayload={setClosedCompetitionsPayload}
              status="Closed"
              selectedUser={selectedUser}
              loading={closedCompetitionsLoading}
            />
          )}
        </Stack>
      </MDBox>
    </DashboardLayout>
  );
}

export default Competitions;
